import { useCallback, useEffect, type ComponentProps } from "react";
import { useContextState } from "../../../state/use-context-state";
import { redemptionError } from "../../../wallet/redemption-diagnostics";
import { transactionUnresolved } from "../../../wallet/transaction-tracker";
import { ELYSIUM } from "../../../config/elysium";
import type { NftView } from "../types";
import type { NftPresentationModel, NftPresentationFeedback } from "../presentation-types";
import type { NftBrowserProps } from "../components/NftBrowser";
import type { NftDetailProps } from "../components/NftDetailSheet";
import type { InventoryHistoryDrawer } from "../components/InventoryHistoryDrawer";

/** Terminal-local UI state; API readers and transaction execution remain host-owned. */
export function useNftPresentation(model: NftPresentationModel, { invoke, notify }: NftPresentationFeedback) {
  const [ui, setUi] = useContextState(model.contextKey, createNftUiState);
  const updateUi = useCallback((patch: Partial<ReturnType<typeof createNftUiState>>) =>
    setUi((current) => ({ ...current, ...patch })), [setUi]);
  const { selectedNftId, lookupTokenId, historyOpen, finalizeBusy, finalizePending,
    redeemBusy, redeemPending, redeemError, redeemRevision } = ui;
  const demoMode = model.demoMode;
  const inspectNft = model.actions.inspectNft;
  const cancelRedeemReview = model.actions.cancelRedeemReview;
  const closeNft = useCallback(() => {
    redeemRevision.current++;
    cancelRedeemReview?.(); updateUi({ selectedNftId: null, redeemError: null }); inspectNft?.(null);
  }, [inspectNft, cancelRedeemReview, updateUi, redeemRevision]);
  const closeHistory = useCallback(() => updateUi({ historyOpen: false }), [updateUi]);

  async function redeem(tokenId: string, minimumPayoutWei: string) {
    if (redeemPending.current || model.transactions.blocked) return;
    redeemPending.current = true;
    const revision = ++redeemRevision.current;
    updateUi({ redeemBusy: true, redeemError: null });
    try {
      if (demoMode) { await model.actions.redeemNft(tokenId, minimumPayoutWei); closeNft(); }
      else {
        if (!model.redeemReview || !model.actions.confirmRedeem) throw new Error("Prepare and review redemption first.");
        await model.actions.confirmRedeem(model.redeemReview.id);
      }
      if (demoMode) notify({ type: "info", message: "Demo redemption complete. No NFT was burned and no ETH moved." });
    } catch (error) {
      if (redeemRevision.current === revision) updateUi({ redeemError: { tokenId,
        message: redemptionError(error, "Redemption could not be completed.") } });
    } finally {
      redeemPending.current = false;
      updateUi({ redeemBusy: false });
    }
  }

  async function prepareRedemption(tokenId: string) {
    if (redeemPending.current || model.transactions.blocked || !model.actions.prepareRedeem) return;
    redeemPending.current = true;
    const revision = ++redeemRevision.current;
    updateUi({ redeemBusy: true, redeemError: null });
    try { await model.actions.prepareRedeem(tokenId); }
    catch (error) {
      if (redeemRevision.current === revision) updateUi({ redeemError: { tokenId,
        message: redemptionError(error, "Redemption preview could not be prepared.") } });
    }
    finally { redeemPending.current = false; updateUi({ redeemBusy: false }); }
  }

  const selectedNft = selectedNftId === null ? null :
    model.inventory.items?.find((item) => item.tokenId === selectedNftId) ?? null;
  const observedTokenId = selectedNftId;
  useEffect(() => {
    inspectNft?.(observedTokenId);
    return () => { inspectNft?.(null); };
  }, [inspectNft, observedTokenId]);

  function selectNft(nft: NftView) {
    cancelRedeemReview?.();
    redeemRevision.current++;
    updateUi({ selectedNftId: nft.tokenId, redeemError: null });
    // Direct detail already includes the pool payout. The full redemption
    // preflight is needed only when preparing/reconfirming the user's review.
    if (!demoMode && model.chainId === ELYSIUM.chainId) return;
    invoke(async () => {
      try { await model.actions.requestRedeemPreview(nft.tokenId); }
      catch (error) { throw new Error(redemptionError(error, "A fresh redemption preview is unavailable.")); }
    }, "A fresh redemption preview is unavailable.");
  }

  async function finalize(tokenId: string) {
    if (finalizePending.current || model.transactions.blocked || !model.actions.finalizeNft) return;
    finalizePending.current = true;
    updateUi({ finalizeBusy: true });
    try { await model.actions.finalizeNft(tokenId); }
    finally { finalizePending.current = false; updateUi({ finalizeBusy: false }); }
  }

  const browserProps: NftBrowserProps = {
    inventory: model.inventory, wallet: model.wallet, onSelect: selectNft,
    onOpenHistory: () => updateUi({ historyOpen: true }),
    onRefresh: () => invoke(model.actions.refreshInventory, "Inventory could not be updated."),
    onLoadMore: () => invoke(model.actions.loadMoreNfts, "More NFTs could not be loaded."),
    lookup: !demoMode && inspectNft ? {
      tokenId: lookupTokenId,
      onChange: (tokenId) => updateUi({ lookupTokenId: tokenId }),
      onSubmit: () => invoke(() => {
        redeemRevision.current++; cancelRedeemReview?.(); inspectNft(lookupTokenId.trim());
        updateUi({ selectedNftId: lookupTokenId.trim(), redeemError: null });
      }, "Invalid NFT token ID."),
    } : null,
  };
  const detailProps: NftDetailProps = {
    nft: selectedNft,
    demoMode,
    verifiedRedemption: model.transactions.redemptions?.find(item => item.tokenId === selectedNftId),
    onCheckRedemption: (id) => invoke(() => model.actions.checkTransaction(id), "Redemption confirmation could not be checked."),
    walletAddress: model.wallet.address,
    redeemReview: model.redeemReview ?? null,
    redeemError: redeemError?.tokenId === selectedNftId ? redeemError.message : null,
    onPrepareRedeem: (id) => invoke(() => prepareRedemption(id), "Redemption preview could not be prepared."),
    onCancelRedeem: () => { redeemRevision.current++; cancelRedeemReview?.(); updateUi({ redeemError: null }); },
    selectedTokenId: selectedNftId,
    chainId: model.chainId,
    finalizePermission: model.finalizePermission,
    finalizeBusy,
    samplingRequired: model.transactions.items.some((item) => item.kind === "FINALIZE" && item.tokenId === selectedNftId && item.phase === "SAMPLING_REQUIRED"),
    samplingExhausted: model.transactions.items.some((item) => item.kind === "FINALIZE" && item.tokenId === selectedNftId && item.phase === "SAMPLING_EXHAUSTED"),
    finalizeNotice: model.finalizeNotice ?? null,
    onFinalize: (id) => invoke(() => finalize(id), "Finalization could not be completed."),
    onRefresh: () => model.actions.refreshNft?.(),
    latestRead: model.nftRead ?? null,
    walletTransactionsEnabled: model.walletTransactionsEnabled,
    busy: redeemBusy,
    onClose: closeNft,
    transactionBlocked: model.transactions.blocked,
    transactionMessage: model.transactions.blocked ? model.transactions.items.find(transactionUnresolved)?.message : undefined,
    onRedeem: (id, minimum) => invoke(() => redeem(id, minimum), "Redemption could not be completed."),
  };
  const historyProps: ComponentProps<typeof InventoryHistoryDrawer> = {
    open: historyOpen, inventory: model.inventory, onClose: closeHistory,
  };
  return { browserProps, detailProps, historyProps };
}

function createNftUiState() {
  return {
    selectedNftId: null as string | null, lookupTokenId: "", historyOpen: false,
    redeemError: null as { tokenId: string; message: string } | null,
    redeemBusy: false, finalizeBusy: false,
    // Immediate guards belong to the same context generation as the dialogs.
    redeemPending: { current: false }, redeemRevision: { current: 0 },
    finalizePending: { current: false },
  };
}
